import { camelizeKeys } from 'humps';
import api from './api';
import * as docPartnerApi from 'features/document/edit/partners/api';
import * as actionTypes from './actionTypes';
import * as eventTypes from './eventTypes';
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from 'actions/actionCreators/httpActions';
import { showError } from 'actions/actionCreators/snackbarActions';
import { loadPersistedPropertyDetailsState, savePersistedPropertyDetailsState } from './localStorage';
import { getDynamicFieldControlType } from 'utils/functions';
import { ControlType } from '@totalsoft/rocket-ui';
import { format } from 'date-fns';

export function loadProperties(partnerId, documentId, siteId) {
  return async (dispatch, getState) => {
    const properties = getState().currentOffer.clientPropertiesData[partnerId];
    let shouldCallApi = !properties || (!properties.loaded && !properties.loading);

    if (!shouldCallApi) {
      return;
    }

    dispatch({ type: actionTypes.LOAD_PROPERTIES_STARTED, key: partnerId });

    try {
      const response = await dispatch(sendHttpRequest(docPartnerApi.getProperties(partnerId, documentId, siteId)));
      const result = camelizeKeys(response);
      dispatch({
        type: actionTypes.LOAD_PROPERTIES_SUCCEEDED,
        key: partnerId,
        properties: result
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_PROPERTIES_FAILED, key: partnerId, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadPropDetails(propertyId) {
  return async (dispatch, getState) => {
    const state = getState();
    const persistedPropertyDetailsState = loadPersistedPropertyDetailsState();
    let propertyDetails = state.entities.offerPropertiesDetails ? state.entities.offerPropertiesDetails[propertyId] : '';

    if (!propertyDetails && !!persistedPropertyDetailsState) {
      propertyDetails = persistedPropertyDetailsState[propertyId];
    }
    if (propertyDetails) {
      return propertyDetails;
    }
    dispatch({ type: actionTypes.LOAD_PROPERTY_DETAILS_STARTED, propertyId });

    try {
      const response = await dispatch(sendHttpRequest(api.getPropertyDetails(propertyId)));
      const result = camelizeKeys(response);
      dispatch({
        type: actionTypes.LOAD_PROPERTY_DETAILS_SUCCEEDED,
        propertyId,
        propertyDetails: result
      });

      const prevPersistedState = loadPersistedPropertyDetailsState();
      savePersistedPropertyDetailsState({
        ...prevPersistedState,
        [propertyId]: [...result]
      });

      return response;
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_PROPERTY_DETAILS_FAILED, error });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function savePartnerProperties(documentId, siteId, partnerId, properties) {
  return async dispatch => {
    dispatch({ type: actionTypes.UPDATE_PROPERTIES_STARTED, payload: properties, key: partnerId });

    const formattedProperties = properties.map(prop => ({
      ...prop,
      propertyValue:
        getDynamicFieldControlType(prop.propertyTypeCode) === ControlType.Date && prop.propertyValue
          ? format(new Date(prop.propertyValue), 'yyyy-MM-dd').toString()
          : prop.propertyValue
    }));

    try {
      let event = await dispatch(
        sendHttpCommandAndWaitForEvent(
          docPartnerApi.savePartnerProperties(documentId, siteId, partnerId, formattedProperties),
          'saveProperties',
          [eventTypes.DOCUMENT_PARTNER_PROPERTIES_UPDATED]
        )
      );

      const actionType = event.ok ? actionTypes.UPDATE_PROPERTIES_SUCCEEDED : actionTypes.UPDATE_PROPERTIES_FAILED;
      const actionPayload = event.ok ? { properties: event.eventBody.properties } : { properties, error: event.error };

      dispatch({ type: actionType, payload: actionPayload, key: partnerId });

      if (!event.ok) throw event.error;
    } catch (error) {
      dispatch({ type: actionTypes.UPDATE_PROPERTIES_FAILED, payload: { properties, error }, key: partnerId });
      throw error;
    }
  };
}
